import { RubyGemMark } from '../components/RubyGemMark';
import { useState, useEffect } from 'react';
import {
  Activity,
  RefreshCw,
  Filter,
  Calendar,
  User,
  Package,
  Building2,
  Box,
  Settings,
  ChevronDown,
  Download,
  Search,
} from 'lucide-react';
// ActivityLog type (formerly from api.ts)
interface ActivityLog {
  id: string;
  type: string;
  action: string;
  resourceType: string;
  resourceId: string;
  performedBy: string;
  details: Record<string, unknown>;
  timestamp: string;
}
import { loadingService } from '../services/loadingService';
import { toastService } from '../services/toastService';
import SearchableDropdown from '../components/SearchableDropdown';
import { StaffPage } from '../components/staff/StaffChrome';

// Mock activity data
const mockActivities: ActivityLog[] = [
  {
    id: '1',
    type: 'tenant_created',
    action: 'created',
    resourceType: 'tenant',
    resourceId: 'tenant_123',
    performedBy: 'admin@cafemenumaster.com',
    details: { businessName: 'Sunrise Coffee', ownerEmail: 'john@sunrise.com' },
    timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    type: 'package_assigned',
    action: 'assigned',
    resourceType: 'package',
    resourceId: 'pkg_creator',
    performedBy: 'admin@cafemenumaster.com',
    details: { packageName: 'Creator Studio', tenantName: 'Metro Bistro', tenantId: 'tenant_456' },
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    type: 'package_removed',
    action: 'removed',
    resourceType: 'package',
    resourceId: 'pkg_team',
    performedBy: 'admin@cafemenumaster.com',
    details: { packageName: 'Team Management', tenantName: 'Test Cafe', tenantId: 'tenant_789' },
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    type: 'tenant_product_changed',
    action: 'updated',
    resourceType: 'tenant',
    resourceId: 'tenant_101',
    performedBy: 'admin@cafemenumaster.com',
    details: { businessName: 'AgenticPOS Demo', previousProduct: 'cafemaster', newProduct: 'pos' },
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    type: 'legacy_plan_toggled',
    action: 'updated',
    resourceType: 'tenant',
    resourceId: 'tenant_102',
    performedBy: 'system',
    details: { businessName: 'Legacy Coffee', legacyPlan: true },
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '6',
    type: 'product_updated',
    action: 'updated',
    resourceType: 'product',
    resourceId: 'cafemaster',
    performedBy: 'admin@cafemenumaster.com',
    details: { productName: 'CafeMaster Pro', field: 'theme' },
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '7',
    type: 'package_created',
    action: 'created',
    resourceType: 'package',
    resourceId: 'pkg_new',
    performedBy: 'admin@cafemenumaster.com',
    details: { packageName: 'New Feature Pack', category: 'shared' },
    timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  },
];

// Light tints per the ruby-chrome standard (dark /20 tints read as muddy on white).
const activityTypeConfig: Record<string, { icon: typeof Activity; color: string; label: string }> = {
  tenant_created: { icon: Building2, color: 'bg-emerald-100 text-emerald-700', label: 'Tenant Created' },
  package_assigned: { icon: Package, color: 'bg-blue-100 text-blue-700', label: 'Package Assigned' },
  package_removed: { icon: Package, color: 'bg-red-100 text-red-700', label: 'Package Removed' },
  package_created: { icon: Package, color: 'bg-rose-100 text-rose-700', label: 'Package Created' },
  package_updated: { icon: Package, color: 'bg-amber-100 text-amber-700', label: 'Package Updated' },
  tenant_product_changed: { icon: Box, color: 'bg-rose-100 text-rose-700', label: 'Product Changed' },
  legacy_plan_toggled: { icon: Settings, color: 'bg-amber-100 text-amber-700', label: 'Legacy Plan Changed' },
  product_updated: { icon: Box, color: 'bg-teal-100 text-teal-700', label: 'Product Updated' },
};

function formatTimestamp(timestamp: string): { date: string; time: string; relative: string } {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let relative = '';
  if (diffMins < 60) relative = `${diffMins}m ago`;
  else if (diffHours < 24) relative = `${diffHours}h ago`;
  else if (diffDays < 7) relative = `${diffDays}d ago`;
  else relative = date.toLocaleDateString();

  return {
    date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    relative,
  };
}

function formatActivityMessage(activity: ActivityLog): string {
  const details = activity.details as Record<string, unknown>;

  switch (activity.type) {
    case 'tenant_created':
      return `Created tenant "${details.businessName}"`;
    case 'package_assigned':
      return `Assigned "${details.packageName}" to ${details.tenantName}`;
    case 'package_removed':
      return `Removed "${details.packageName}" from ${details.tenantName}`;
    case 'package_created':
      return `Created package "${details.packageName}"`;
    case 'package_updated':
      return `Updated package "${details.packageName}"`;
    case 'tenant_product_changed':
      return `Changed ${details.businessName} from ${details.previousProduct} to ${details.newProduct}`;
    case 'legacy_plan_toggled':
      return `${details.legacyPlan ? 'Enabled' : 'Disabled'} legacy plan for ${details.businessName}`;
    case 'product_updated':
      return `Updated product "${details.productName}" (${details.field})`;
    default:
      return `${activity.action} ${activity.resourceType} ${activity.resourceId}`;
  }
}

function ActivityLogPage() {
  const [activities, setActivities] = useState<ActivityLog[]>(mockActivities);
  const [isLoading, setIsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement real API call when activity endpoint is available
      // Filter mock data based on current filter
      let filtered = mockActivities;
      if (typeFilter !== 'all') {
        filtered = filtered.filter(a => a.type === typeFilter);
      }
      setActivities(filtered);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [typeFilter]);

  const handleRefresh = () => {
    loadingService.start();
    fetchActivities().finally(() => loadingService.stop());
    toastService.info('Refreshing activity log...');
  };

  const filteredActivities = activities.filter((activity) => {
    if (!searchQuery) return true;
    const message = formatActivityMessage(activity).toLowerCase();
    const performer = activity.performedBy.toLowerCase();
    return message.includes(searchQuery.toLowerCase()) || performer.includes(searchQuery.toLowerCase());
  });

  const uniqueTypes = Array.from(new Set(activities.map((a) => a.type)));

  return (
    <StaffPage
      section="System"
      icon={Activity}
      title="Activity Log"
      subtitle="Audit trail of all platform actions"
      actions={
        <>
          <button
            onClick={() => toastService.info('Export feature coming soon')}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </>
      }
    >
      <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-300 rounded-lg text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-admin-primary"
          />
        </div>
        <div className="flex items-center gap-2 min-w-[220px]">
          <Filter className="w-4 h-4 text-stone-500 shrink-0" />
          <div className="flex-1">
            <SearchableDropdown
              options={[
                { value: 'all', label: 'All Types' },
                ...uniqueTypes.map((type) => ({
                  value: type,
                  label: activityTypeConfig[type]?.label || type,
                })),
              ]}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v || 'all')}
              placeholder="All Types"
            />
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="font-semibold text-stone-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-admin-primary" />
            Recent Activity
          </h2>
          <span className="text-sm text-stone-500">
            {filteredActivities.length} {filteredActivities.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        <div className="divide-y divide-stone-200">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-600">No activity found</p>
              <p className="text-sm text-stone-500 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            filteredActivities.map((activity) => {
              const config = activityTypeConfig[activity.type] || {
                icon: Activity,
                color: 'bg-stone-500/20 text-stone-400',
                label: activity.type,
              };
              const Icon = config.icon;
              const time = formatTimestamp(activity.timestamp);

              return (
                <div key={activity.id} className="px-5 py-4 hover:bg-stone-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-stone-900 font-medium">{formatActivityMessage(activity)}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${config.color}`}>
                              {config.label}
                            </span>
                            <span className="text-xs text-stone-500 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {activity.performedBy}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-stone-600">{time.relative}</p>
                          <p className="text-xs text-stone-400">{time.time}</p>
                        </div>
                      </div>

                      {/* Details Preview */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(activity.details || {}).slice(0, 3).map(([key, value]) => (
                          <span
                            key={key}
                            className="text-xs px-2 py-0.5 bg-stone-100 text-stone-600 rounded"
                          >
                            {key}: {String(value)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Load More */}
        {filteredActivities.length >= 10 && (
          <div className="px-5 py-4 border-t border-stone-200 text-center">
            <button
              onClick={() => toastService.info('Pagination coming soon')}
              className="text-sm text-admin-primary hover:text-admin-secondary transition-colors"
            >
              Load More Activities
            </button>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
        <h3 className="font-semibold text-stone-900 mb-2">About Activity Logs</h3>
        <p className="text-stone-600 text-sm">
          Activity logs provide a complete audit trail of all actions performed on the platform.
          This includes tenant management, package assignments, product changes, and system events.
          Logs are retained for 90 days and can be exported for compliance purposes.
        </p>
      </div>
      </div>
    </StaffPage>
  );
}

export default ActivityLogPage;
